// Merchant quote-requests inbox. Lists requests addressed to this merchant OR
// open requests in their city. "Send quote" opens the quote builder, where the
// merchant prices the cart and sends a quote to the customer.
//
// On DataTable, per Web Portal v2 (TABLES.m_inbox). The mobile card stack
// survives via renderCard — this is the screen a merchant checks on a phone
// between customers, so horizontal scrolling would be the wrong trade.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, ArrowRight } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { MERCHANT_NAV } from '../_navs';
import { useMerchantNav } from '../../lib/pendingAction';
import { requests, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const QUOTE_PENDING_STATUSES = ['submitted', 'quote_pending'];

/** "18m ago" is what a merchant needs; a timestamp makes them do the maths. */
function ageOf(created: string) {
  const m = Math.max(0, Math.floor((Date.now() - new Date(created).getTime()) / 60000));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`;
}

export function MerchantQuoteRequestsPage() {
  // Pull EVERYTHING the merchant can see, then filter client-side.
  const q = useQuery({ queryKey: ['merchant', 'incoming'], queryFn: () => requests.list() });

  const inbox = useMemo(
    () => (q.data ?? []).filter((r) => QUOTE_PENDING_STATUSES.includes(r.status)),
    [q.data],
  );

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useMerchantNav(MERCHANT_NAV);

  // Only the waiting count is real. The design also shows median response
  // time and acceptance rate; neither has backing data, so they are omitted
  // rather than estimated.
  const summary = useMemo(() => {
    if (inbox.length === 0) return undefined;
    const oldest = inbox.reduce((a, r) => (r.created_at < a ? r.created_at : a), inbox[0].created_at);
    return [{ label: 'Waiting on you', value: String(inbox.length), hint: `Oldest ${ageOf(oldest)} ago` }];
  }, [inbox]);

  const columns: Column<ServiceRequestWithItems>[] = [
    {
      key: 'ref', header: 'Reference', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}`,
    },
    {
      key: 'for', header: 'Recipient', type: 'strong', width: 'minmax(0,1.3fr)',
      value: (r) => r.recipient?.full_name ?? '—',
    },
    {
      key: 'city', header: 'Area', type: 'text', width: 'minmax(0,1fr)',
      value: (r) => r.city?.name ?? '—',
    },
    {
      key: 'items', header: 'Items', type: 'money', width: '70px',
      value: (r) => r.cart_item_count,
    },
    {
      key: 'goods', header: 'Est. goods', type: 'money', width: '120px',
      value: (r) => (r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'),
    },
    {
      key: 'age', header: 'Received', type: 'muted', width: '100px',
      value: (r) => `${ageOf(r.created_at)} ago`,
    },
    {
      key: 'go', header: '', type: 'node', width: '130px',
      value: (r) => (
        <Link
          to={`/merchant/quote-requests/${r.id}`}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-700"
        >
          Send quote <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      ),
    },
  ];

  return (
    <PortalShell
      nav={nav}
      activeId="inbox"
      title="Quote requests"
      subtitle="Customers are waiting. Confirm stock and send a price."
    >
      <DataTable
        title="Inbox"
        subtitle="Oldest first — quotes expire after 24 hours."
        summary={summary}
        columns={columns}
        rows={inbox}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        empty="Inbox is empty. Requests from customers in your city land here."
        renderCard={(r) => (
          <Link to={`/merchant/quote-requests/${r.id}`} className="block active:bg-[var(--color-panel)]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] text-[var(--color-body)]">
                REQ-{String(r.id).padStart(4, '0')}
              </span>
              <StatusPill status={r.status} />
            </div>
            <p className="mt-1 text-[13.5px] font-bold text-[var(--color-ink)]">
              {r.recipient?.full_name ?? '—'}
              {r.city?.name ? ` · ${r.city.name}` : ''}
            </p>
            <p className="text-[12px] text-[var(--color-muted)]">
              {r.cart_item_count} item{r.cart_item_count === 1 ? '' : 's'}
              {r.cart_subtotal_htg_minor > 0 ? ` · ${formatHtgMinor(r.cart_subtotal_htg_minor)}` : ''}
            </p>
            <div className="mt-1.5 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-muted)]">
                <Clock className="h-3 w-3" aria-hidden="true" /> {ageOf(r.created_at)} ago
              </span>
              <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600">
                Send quote <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </div>
          </Link>
        )}
      />
    </PortalShell>
  );
}
